import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { OrderApi } from '@citypantry/shared-api';
import { AppConfig } from '@citypantry/shared-app-config';
import { AuthSelectors } from '@citypantry/shared-auth';
import { AppState } from '@citypantry/state';
import { MediaQueryService, ScreenSizes, WindowRef } from '@citypantry/util-browser';
import { getOrderIdFromUrlPath } from '@citypantry/util-models';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { ChatWidgetType, ChatWidgetTypes } from './chat-widget-type.enum';

export const ACTIVE_CHAT_WIDGET = 'activeChatWidget';

const CHECK_INTERVAL_FOR_ZOPIM_MS = 100;
const MAX_WAIT_TIME_FOR_ZOPIM_MS = 60_000;

@Injectable({
  providedIn: 'root',
})
export class ChatWidgetService {
  constructor(
    private windowRef: WindowRef,
    private appConfig: AppConfig,
    private mediaQueryService: MediaQueryService,
    private store: Store<AppState>,
    private location: Location,
    private orderApi: OrderApi,
  ) {}

  /**
   * Initialise the chat widget if the user is a customer, orderer or vendor
   */
  public initialiseChat(): void {
    if (this.appConfig.INCLUDE_ZENDESK === false || this.mediaQueryService.isBelow(ScreenSizes.TABLET)) {
      return;
    }

    combineLatest([
      this.store.select(AuthSelectors.isCustomerOrOrderer),
      this.store.select(AuthSelectors.isVendor),
    ]).pipe(
      take(1)
    ).subscribe(([isCustomerOrOrderer, isVendor]) => {
      if (isCustomerOrOrderer || isVendor) {
        this.initZendeskMessenger();
      }

      return;
    });
  }

  public setChatApp(targetWidget: ChatWidgetType): void {
    switch (targetWidget) {
      case ChatWidgetTypes.ZENDESK_MESSENGER:
        this.windowRef.nativeWindow.sessionStorage.setItem(ACTIVE_CHAT_WIDGET, ChatWidgetTypes.ZENDESK_MESSENGER);
        break;
      case ChatWidgetTypes.ZOPIM:
        this.windowRef.nativeWindow.sessionStorage.setItem(ACTIVE_CHAT_WIDGET, ChatWidgetTypes.ZOPIM);
        break;
      case ChatWidgetTypes.NONE:
        this.windowRef.nativeWindow.sessionStorage.setItem(ACTIVE_CHAT_WIDGET, ChatWidgetTypes.NONE);
        break;
      default:
        this.windowRef.nativeWindow.sessionStorage.setItem(ACTIVE_CHAT_WIDGET, ChatWidgetTypes.NONE);
    }
  }

  public openSupportChat(): void {
    this.initZendeskMessenger((zE: ZendeskMessengerFunction) => {
      zE('messenger', 'open');
    });
  }

  private initZendeskMessenger(callback: (zE: ZendeskMessengerFunction) => void = null): void {
    this.waitForZEFunction((zE: ZendeskMessengerFunction) => {
      this.setChatApp(ChatWidgetTypes.ZENDESK_MESSENGER);
      zE('messenger', 'show');
      zE('messenger:on', 'open', () => {
        this.getCurrentOrderHumanIds().subscribe((humanOrderIds: string[]) => {
          humanOrderIds = humanOrderIds.map((id) => `#${ id }`);
          zE('messenger:set', 'conversationFields', [{id: this.appConfig.ZENDESK_ORDER_NUMBER_FIELD_ID, value: `${humanOrderIds.join(', ')}`}]);
        });
      });

      if (callback) {
        callback(zE);
      }
    });
  }

  private waitForZEFunction(callback: (zE: any) => void): void {
    let checkCount = 0;

    const getZEFunctionFromWindow = () => {
      const zE = this.windowRef.nativeWindow['zE'];

      if (zE) {
        callback(zE);
      }

      return zE;
    };

    // If zE function is not loaded then poll it every 100ms until truthy or 60 seconds pass
    if (!getZEFunctionFromWindow()) {
      const waitForWidget = setInterval(() => {
        if (checkCount > (MAX_WAIT_TIME_FOR_ZOPIM_MS / CHECK_INTERVAL_FOR_ZOPIM_MS)) {
          clearInterval(waitForWidget);
          return;
        }

        const zE = getZEFunctionFromWindow();

        if (!zE) {
          checkCount++;
        } else {
          clearInterval(waitForWidget);
        }
      }, CHECK_INTERVAL_FOR_ZOPIM_MS);
    }
  }

  private getCurrentOrderHumanIds(): Observable<string[]> {
    const currentUrlPath = this.location.path(false).split('?', 2)[0];
    const orderId = getOrderIdFromUrlPath(currentUrlPath);    // single order OR IC order group

    if (orderId) {
      return this.orderApi.getOrderHumanIds(orderId);
    }
    return of([]);
  }
}

/**
 * ==============================================================================================
 * NOTE: No types for zE function exist. This is built from https://developer.zendesk.com/api-reference/widget-messaging/web/core/, but doesn't cover "Set Customize" or
 */
type ZendeskMessengerFunction = (action: string, detail: string, arg?: ZendeskMessengerArgument) => void;
type ZendeskMessengerArgument = (() => void) | ((count: number) => void) | ZendeskMessengerConversationFields | number | string | string[];
type ZendeskMessengerConversationFields = { id: string, value: string | number | boolean }[];
